// Shared formatting for odds, implied probabilities, and model-vs-market edges.
// Used by the games schedule card and the simulation-detail market panel so
// edges are displayed identically everywhere.

/** American odds as a signed string, or em-dash when null. */
export function formatOdds(price: number | null): string {
  if (price === null) return '—'
  return price >= 0 ? `+${price}` : `${price}`
}

/** A 0-1 probability as a percentage string (one decimal), or em-dash. */
export function formatPct(val: number | null): string {
  if (val === null) return '—'
  return `${(val * 100).toFixed(1)}%`
}

/** A 0-1 edge as a signed percentage string (one decimal), or em-dash. */
export function formatEdge(val: number | null): string {
  if (val === null) return '—'
  const pct = val * 100
  return pct >= 0 ? `+${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`
}

/** CSS class for an edge: positive (favorable), negative, or none when null. */
export function edgeClass(val: number | null): string {
  if (val === null) return ''
  return val >= 0 ? 'edge--positive' : 'edge--negative'
}

/** Human label for a PropMarketType value, e.g. "pitcher_strikeouts" -> "K (P)". */
export function propMarketLabel(market: string): string {
  const labels: Record<string, string> = {
    pitcher_strikeouts: 'Strikeouts (P)',
    pitcher_walks: 'Walks (P)',
    pitcher_hits_allowed: 'Hits Allowed (P)',
    pitcher_earned_runs: 'Earned Runs (P)',
    batter_hits: 'Hits',
    batter_home_runs: 'Home Runs',
    batter_rbis: 'RBIs',
    batter_runs_scored: 'Runs',
    batter_walks: 'Walks',
    batter_strikeouts: 'Strikeouts'
  }
  return labels[market] ?? market
}

/**
 * Convert a 0–1 probability to American odds (rounded to nearest integer).
 * Returns null for probabilities at or beyond 0 / 1.
 */
export function pctToOdds(p: number | null): number | null {
  if (p === null || p <= 0 || p >= 1) return null
  if (p >= 0.5) {
    return Math.round((-p / (1 - p)) * 100)
  }
  return Math.round(((1 - p) / p) * 100)
}

/** A 0-1 probability formatted as American odds, or em-dash when null. */
export function formatPctAsOdds(val: number | null): string {
  return formatOdds(pctToOdds(val))
}

/** Short label for a prop chip, e.g. "pitcher_strikeouts" -> "K". */
export function propMarketShort(market: string): string {
  const labels: Record<string, string> = {
    pitcher_strikeouts: 'K',
    pitcher_walks: 'BB',
    pitcher_hits_allowed: 'H',
    pitcher_earned_runs: 'ER',
    batter_hits: 'H',
    batter_home_runs: 'HR',
    batter_rbis: 'RBI',
    batter_runs_scored: 'R',
    batter_walks: 'BB',
    batter_strikeouts: 'K'
  }
  return labels[market] ?? market
}
